import { ApolloLink } from '@apollo/client/core';
import { Observable } from 'rxjs';
import { print } from 'graphql';
import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import {
  AwsCredentials,
  AwsCredentialsService,
} from 'src/app/shared/services/aws-credentials/aws-credentials.service';

/**
 * Resolve the signing region from an AppSync endpoint URL.
 * - Format: https://<id>.appsync-api.<region>.amazonaws.com/graphql
 * - ca custom domains: *.ca.amira.cloud → ca-central-1
 *
 * Fallback: Use the identity pool's region (credentialsService.region),
 * or use the provided fallback region if all else fails.
 * @param endpoint AppSync endpoint URL
 * @param fallbackRegion fallback region
 * @returns the signing region
 */
export function resolveSigningRegion(
  endpoint: string,
  fallbackRegion: string
): string {
  const url = new URL(endpoint);
  const stdMatch = url.hostname.match(
    /\.appsync-api\.([^.]+)\.amazonaws\.com$/
  );
  if (stdMatch) {
    return stdMatch[1];
  }

  const caMatch = url.hostname.match(/\.ca\.amira\.cloud$/);
  if (caMatch) {
    return 'ca-central-1';
  }

  return fallbackRegion;
}

/**
 * Create an Apollo Link that SigV4-signs every request with temporary AWS credentials.
 *
 * On 403 errors (stale credentials), invalidate the credentials cache and retry once.
 *
 * This supports Apollo-level cancellation (component unmount, query deduplication, client.stop()) via
 * the returned Observable's teardown and aborts after timeoutMs so that a request doesn't block forever.
 */
export function createSigV4Link(
  endpoint: string,
  credentialsService: AwsCredentialsService,
  timeoutMs = 120000
): ApolloLink {
  return new ApolloLink(
    (operation: ApolloLink.Operation, _forward: ApolloLink.ForwardFunction) => {
      return new Observable<ApolloLink.Result>(observer => {
        const controller = new AbortController();
        let settled = false;
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        (async (): Promise<void> => {
          try {
            const url = new URL(endpoint);
            const hostname = url.hostname;
            const path = url.pathname;

            const body = JSON.stringify({
              query: print(operation.query),
              variables: operation.variables,
              operationName: operation.operationName,
            });

            let credsUsed: AwsCredentials | undefined;

            const doRequest = async (): Promise<Response> => {
              const creds = await credentialsService.getCredentials();
              credsUsed = creds;
              const region = resolveSigningRegion(
                endpoint,
                credentialsService.region
              );

              const signer = new SignatureV4({
                credentials: {
                  accessKeyId: creds.accessKeyId,
                  secretAccessKey: creds.secretAccessKey,
                  sessionToken: creds.sessionToken,
                },
                region,
                service: 'appsync',
                sha256: Sha256,
              });

              const request = new HttpRequest({
                method: 'POST',
                hostname,
                path,
                headers: {
                  'Content-Type': 'application/json',
                  host: hostname,
                },
                body,
              });

              const signed = await signer.sign(request);

              try {
                return await fetch(endpoint, {
                  method: 'POST',
                  headers: signed.headers as Record<string, string>,
                  body,
                  signal: controller.signal,
                });
              } catch (error) {
                // If the fetch threw a TypeError on network failure, set retryable statusCode 0 (matches API key auth)
                if (error instanceof TypeError) {
                  throw Object.assign(error, { statusCode: 0 });
                }

                throw error;
              }
            };

            let response = await doRequest();

            // Retry once for 403 (stale credentials). Only invalidate the cached credentials first
            // if they're still the ones used in the request that just failed.
            if (response.status === 403) {
              if (
                credentialsService.currentCredentials?.accessKeyId ===
                credsUsed?.accessKeyId
              ) {
                credentialsService.invalidate();
              }
              response = await doRequest();
            }

            if (!response.ok) {
              const text = await response.text();
              throw Object.assign(
                new Error(`AppSync request failed (${response.status})`),
                { statusCode: response.status, error: text }
              );
            }

            const json = await response.json();

            if (settled) {
              return;
            }

            observer.next(json);
            observer.complete();
          } catch (error) {
            if (settled) {
              return;
            }

            if (error instanceof DOMException && error.name === 'AbortError') {
              // Set a non-retryable statusCode so that RetryLink skips retries for timeouts
              observer.error(
                Object.assign(
                  new Error(
                    `AppSync request to ${endpoint} timed out or was cancelled after ${timeoutMs} ms`
                  ),
                  { statusCode: 408 }
                )
              );
            } else {
              observer.error(error);
            }
          } finally {
            clearTimeout(timeoutId);
          }
        })();

        // Apollo calls this on unsubscribe (component unmount, query deduplication cancel, client.stop())
        return () => {
          settled = true;
          clearTimeout(timeoutId);
          controller.abort();
        };
      });
    }
  );
}
